import { useQuery } from '@tanstack/react-query';
import type { ChartPayload } from '@oxxean/types';
import {
  fetchDiversity,
  type DiversityByCompany,
  type DiversityDimension,
} from '../../lib/api/diversity';
import { formatNumber } from '../../lib/format';
import { useFilters } from '../../hooks/useFilters';
import { ChartCard } from '../../components/ChartCard';
import { DataBoundary } from '../../components/DataBoundary';
import { MetricDisplay } from '../../components/MetricDisplay';
import { Card, Notice, TableSkeleton } from '../../components/primitives';
import { PageHeading } from '../layout/PageHeading';

/**
 * Convierte una dimensión en el `ChartPayload` que consume `ChartCard`.
 *
 * ── Por qué se reutiliza `ChartCard` y no se dibuja aquí ───────────────────
 * Ese componente ya resuelve todo lo que este reporte necesita: porcentaje sobre
 * cada porción, total en el centro, leyenda con valores, recuadro al pasar el
 * puntero, y —lo más importante— el tratamiento del ESTADO del dato. Un gráfico con
 * `NOT_AVAILABLE` no se dibuja vacío: muestra el aviso correspondiente.
 *
 * `DiversityDimension.status` encaja exactamente con `ChartPayload.status`, así que
 * una dimensión sin cobertura llega a la pantalla declarada como no disponible sin
 * ninguna traducción intermedia donde pudiera perderse.
 *
 * El ORDEN de las categorías se respeta tal como viene: los tramos etarios llegan
 * ordenados por edad y en el anillo se dibujan en ese orden. Ordenarlos por cantidad
 * -como sí conviene en género o nacionalidad- destruiría la lectura de la pirámide.
 */
function aPayload(
  key: string,
  title: string,
  dimension: DiversityDimension,
  opciones: { ordenarPorCantidad: boolean },
): ChartPayload {
  const categorias = opciones.ordenarPorCantidad
    ? [...dimension.categories].sort((a, b) => b.count - a.count)
    : dimension.categories;

  return {
    key,
    title,
    type: 'doughnut',
    labels: categorias.map((c) => c.label),
    series: [
      {
        key,
        label: title,
        points: categorias.map((c) => ({
          label: c.label,
          value: c.count,
          status: dimension.status,
        })),
      },
    ],
    status: dimension.status,
    note:
      dimension.withoutData > 0
        ? `${formatNumber(dimension.withoutData, 'integer')} colaborador(es) sin este ` +
          'atributo informado. No se reparten entre las categorías: los porcentajes se ' +
          `calculan sobre los ${formatNumber(dimension.withData, 'integer')} que sí lo tienen.`
        : undefined,
  };
}

/**
 * Reporte de Diversidad.
 *
 * ── Por qué cada dimensión muestra su cobertura ────────────────────────────
 * Un reporte de diversidad es exactamente donde un cero inventado hace más daño.
 * «0 % de personas con discapacidad» y «no tenemos ese dato» se ven casi iguales en
 * un gráfico y significan lo contrario: el primero afirma incumplimiento de la Ley
 * 21.015, el segundo dice que nadie lo ha medido.
 *
 * Por eso cada barra viene acompañada de cuántas personas tienen el atributo
 * informado, y las que no lo tienen se cuentan aparte en lugar de repartirse entre
 * las categorías conocidas.
 *
 * ── Barras dibujadas con CSS y no con Chart.js ─────────────────────────────
 * Son distribuciones de una sola serie con etiqueta y porcentaje al lado. Una barra
 * horizontal proporcional se lee igual y permite poner el conteo, el porcentaje y su
 * estado de dato en la misma línea, que es lo que hace falta aquí.
 */
/**
 * Género por empresa: barras horizontales, una serie por género.
 *
 * ── Por qué barras y no un anillo ──────────────────────────────────────────
 * Un anillo responde «cuánto de todo es cada parte» sobre UNA distribución. Aquí hay
 * un cruce de dos dimensiones —empresa por género—, y para eso haría falta un anillo
 * por empresa: seis gráficos que no se pueden comparar entre sí. Con barras se ve de
 * un vistazo que INMOBILIARIA PACHECO reparte 50/50 y PUERTO OXXEAN 85/15, que es la
 * pregunta que este cruce responde.
 *
 * Horizontales porque las razones sociales son largas: «PUERTO OXXEAN CHACABUCO S.A.»
 * en un eje vertical quedaría girada o recortada.
 *
 * ── Por qué la clave termina en `ByCompany` ────────────────────────────────
 * No es decorativo: `ChartCard` reconoce ese sufijo y hace las barras pulsables para
 * filtrar por esa empresa. La convención existe en el backend y aquí se respeta en
 * lugar de inventar una clave nueva que perdería el comportamiento.
 */
function generoPorEmpresaAPayload(filas: DiversityByCompany[]): ChartPayload {
  // Las series se derivan de los géneros PRESENTES, no de una lista fija: si el
  // origen empieza a entregar un valor nuevo, aparece solo en lugar de perderse.
  const generos = new Map<string, string>();
  for (const fila of filas) {
    for (const g of fila.byGender) if (!generos.has(g.key)) generos.set(g.key, g.label);
  }

  return {
    key: 'diversityGenderByCompany',
    title: 'Género por empresa',
    type: 'horizontalBar',
    labels: filas.map((f) => f.company),
    series: [...generos.entries()].map(([key, label]) => ({
      key,
      label,
      points: filas.map((fila) => {
        const encontrado = fila.byGender.find((g) => g.key === key);
        return {
          label: fila.company,
          // `0` y no `null` cuando la empresa no tiene ese género: aquí el cero SÍ
          // está medido —se contaron sus colaboradores y ninguno lo tiene—, y es
          // distinto de «no lo sabemos». Las personas sin género informado quedan
          // fuera del cruce y se declaran en el aviso de la dimensión.
          value: encontrado?.count ?? 0,
          status: 'REAL' as const,
        };
      }),
    })),
    status: 'REAL',
  };
}

export function DiversityPage(): JSX.Element {
  const { filters } = useFilters();

  const reporte = useQuery({
    queryKey: ['diversity', filters],
    queryFn: () => fetchDiversity(filters),
  });

  const d = reporte.data;

  return (
    <>
      <PageHeading
        title="Reporte de Diversidad"
        description="Composición de la dotación vigente por género, edad y nacionalidad."
      />

      <DataBoundary
        isLoading={reporte.isLoading}
        isError={reporte.isError}
        error={reporte.error}
        onRetry={() => void reporte.refetch()}
        skeleton={<TableSkeleton rows={8} />}
      >
        {d && (
          <>
            <Card title="Dotación considerada">
              <p className="lead">
                <strong>{formatNumber(d.total, 'integer')}</strong> colaboradores vigentes en
                el ámbito consultado.
              </p>
              <p className="tiny muted">
                Se consideran quienes están vigentes hoy: un reporte de diversidad describe
                a quienes trabajan en la organización, no a quienes pasaron por ella.
              </p>
            </Card>

            {/* Los tres anillos van en fila: se comparan mejor lado a lado que
                apilados, y cada uno es una distribución independiente. */}
            <div className="row row--3">
              <ChartCard
                payload={aPayload('diversity-gender', 'Género', d.gender, {
                  ordenarPorCantidad: true,
                })}
              />
              <ChartCard
                payload={aPayload('diversity-age', 'Rango etario', d.ageRange, {
                  // NO se ordena por cantidad: los tramos llegan por edad y en el
                  // anillo deben dibujarse en ese orden para poder leerse como
                  // pirámide. Ordenarlos por tamaño la destruiría.
                  ordenarPorCantidad: false,
                })}
              />
              <ChartCard
                payload={aPayload('diversity-nationality', 'Nacionalidad', d.nationality, {
                  ordenarPorCantidad: true,
                })}
              />
            </div>

            {/* El detalle numérico se conserva bajo los gráficos. Un anillo responde
                «cuánto de todo», pero quien va a citar la cifra en un informe
                necesita el número exacto sin tener que apuntar con el puntero. */}
            <Card title="Detalle">
              <Dimension title="Género" dimension={d.gender} total={d.total} />
              <Dimension title="Rango etario" dimension={d.ageRange} total={d.total} ordered />
              <Dimension title="Nacionalidad" dimension={d.nationality} total={d.total} />
            </Card>

            {d.byCompany.length > 0 && (
              <ChartCard payload={generoPorEmpresaAPayload(d.byCompany)} />
            )}

            {d.byCompany.length > 0 && (
              <Card title="Género por empresa · detalle">
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th className="num">Dotación</th>
                      <th>Distribución</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.byCompany.map((fila) => (
                      <tr key={fila.company}>
                        <td>{fila.company}</td>
                        <td className="num">{formatNumber(fila.total, 'integer')}</td>
                        <td>
                          {fila.byGender.map((g) => (
                            <span key={g.key} className="chip">
                              {g.label}: {formatNumber(g.count, 'integer')}
                              {g.share.value !== null && (
                                <> ({formatNumber(g.share.value, 'decimal')} %)</>
                              )}
                            </span>
                          ))}
                          {fila.byGender.length === 0 && (
                            <span className="tiny muted">Sin género informado</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {/* ── Lo que NO se puede informar ───────────────────────────────
                Va en la misma pantalla y con el mismo peso visual que el resto.
                Relegarlo a una nota al pie haría que se leyera como un detalle,
                cuando es la parte del reporte que exige una decisión. */}
            <Card title="Dimensiones sin fuente de datos">
              <div className="metric-block">
                <span className="metric-block__label">{d.disability.label}</span>
                <MetricDisplay metric={d.disability.metric} format="percent" />
                <Notice tone="partial">{d.disability.legalNote}</Notice>
              </div>

              <div className="metric-block">
                <span className="metric-block__label">{d.education.label}</span>
                <MetricDisplay metric={d.education.metric} format="percent" />
              </div>
            </Card>
          </>
        )}
      </DataBoundary>
    </>
  );
}

/**
 * Una dimensión con sus categorías.
 *
 * @param ordered Conserva el orden recibido en lugar de ordenar por cantidad. Los
 *   rangos etarios lo necesitan: una pirámide desordenada deja de ser una pirámide.
 */
function Dimension({
  title,
  dimension,
  total,
  ordered = false,
}: {
  title: string;
  dimension: DiversityDimension;
  total: number;
  ordered?: boolean;
}): JSX.Element {
  const categorias = ordered
    ? dimension.categories
    : [...dimension.categories].sort((a, b) => b.count - a.count);

  const mayor = Math.max(1, ...categorias.map((c) => c.count));

  return (
    // Sección, no tarjeta: va DENTRO de la tarjeta «Detalle». Anidar tarjetas duplica
    // bordes y separaciones, y el segundo marco no aporta ninguna información.
    <section className="dimension">
      <h3 className="dimension__title">{title}</h3>

      {dimension.status === 'NOT_AVAILABLE' ? (
        <Notice tone="partial">
          Ninguno de los {formatNumber(total, 'integer')} colaboradores tiene este atributo
          informado. No se muestran porcentajes: serían un reparto inventado.
        </Notice>
      ) : (
        <>
          {dimension.withoutData > 0 && (
            <Notice tone="partial">
              {formatNumber(dimension.withoutData, 'integer')} de{' '}
              {formatNumber(total, 'integer')} colaboradores no tienen este atributo
              informado. Los porcentajes se calculan sobre los{' '}
              {formatNumber(dimension.withData, 'integer')} que sí lo tienen, y esas personas{' '}
              <strong>no se reparten</strong> entre las categorías.
            </Notice>
          )}

          <ul className="bars">
            {categorias.map((categoria) => (
              <li key={categoria.key} className="bars__item">
                <span className="bars__label">{categoria.label}</span>
                <span className="bars__track">
                  <span
                    className="bars__fill"
                    style={{ width: `${(categoria.count / mayor) * 100}%` }}
                  />
                </span>
                <span className="bars__value">
                  {formatNumber(categoria.count, 'integer')}
                  {categoria.share.value !== null && (
                    <span className="bars__pct">
                      {formatNumber(categoria.share.value, 'decimal')} %
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
